// Debug utility to control console output in production
// Only shows logs in development, preview, or internal domains

// Import consistent domain checking
import { isDebugMode, getNetlifyFunctionUrl } from './environmentUtils.js';

// Debug console that only logs in development environments
export const debugConsole = {
  // Debug logs - only show in development
  log: (...args) => {
    if (isDebugMode()) {
      console.log(...args);
    }
  },

  // Info logs - only show in development
  info: (...args) => {
    if (isDebugMode()) {
      console.info(...args);
    }
  },

  // Warning logs - always show (important for production debugging)
  warn: (...args) => {
    console.warn(...args);
  },

  // Error logs - always show (critical for production debugging)
  error: (...args) => {
    console.error(...args);
  },

  // Success logs - only show in development
  success: (...args) => {
    if (isDebugMode()) {
      console.log('✅', ...args);
    }
  },

  // Failure logs - only show in development (use error() for production-critical failures)
  failure: (...args) => {
    if (isDebugMode()) {
      console.log('❌', ...args);
    }
  },

  // Group logs - only show in development
  group: label => {
    if (isDebugMode()) {
      console.group(label);
    }
  },

  groupEnd: () => {
    if (isDebugMode()) {
      console.groupEnd();
    }
  },
};

// Rate limiting for critical error logging to prevent spam
const errorLogRateLimit = {
  lastSent: 0,
  count: 0,
  windowStart: 0,
  maxPerWindow: 5, // Max 5 critical errors per 5 minutes
  windowDuration: 5 * 60 * 1000, // 5 minutes
};

/**
 * Log critical analytics errors to both console and Netlify function
 * Only use this for truly critical errors that need remote monitoring
 * @param {string} errorType - Type of error (e.g., 'mixpanel_init_failed', 'tracking_failure_pattern')
 * @param {string} message - Error message
 * @param {object} context - Additional context (error object, event data, etc.)
 * @param {string} severity - 'critical', 'error', 'warning', or 'info'
 */
export const logCriticalAnalyticsError = async (
  errorType,
  message,
  context = {},
  severity = 'error'
) => {
  const now = Date.now();

  // Always log to console first
  const logMessage = `[${errorType}] ${message}`;
  switch (severity) {
    case 'critical':
      console.error('🚨 CRITICAL ANALYTICS ERROR:', logMessage, context);
      break;
    case 'error':
      console.error('❌ ANALYTICS ERROR:', logMessage, context);
      break;
    case 'warning':
      console.warn('⚠️ ANALYTICS WARNING:', logMessage, context);
      break;
    default:
      console.log('📊 ANALYTICS INFO:', logMessage, context);
  }

  // Rate limiting check
  if (now - errorLogRateLimit.windowStart > errorLogRateLimit.windowDuration) {
    // New window
    errorLogRateLimit.windowStart = now;
    errorLogRateLimit.count = 1;
  } else if (errorLogRateLimit.count >= errorLogRateLimit.maxPerWindow) {
    // Rate limited
    console.warn('⚠️ Critical error logging rate limited, skipping remote log');
    return;
  } else {
    errorLogRateLimit.count++;
  }

  // Only send to remote in production or when specifically enabled
  if (typeof window === 'undefined' || isDebugMode()) {
    // Skip remote logging in development/preview unless forced
    if (!window?.forceCriticalErrorLogging) {
      debugConsole.log(
        'Skipping remote error logging in development environment'
      );
      return;
    }
  }

  try {
    // Prepare error data
    const errorData = {
      errorType,
      message,
      severity,
      url: typeof window !== 'undefined' ? window.location.href : 'unknown',
      pathname:
        typeof window !== 'undefined' ? window.location.pathname : 'unknown',
      userAgent:
        typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      timestamp: new Date().toISOString(),
      ...context,
    };

    // Send to Netlify function (non-blocking)
    const functionUrl = getNetlifyFunctionUrl('analytics-error-logger');

    // Use fetch with timeout and don't await to avoid blocking
    const timeoutId = setTimeout(() => {
      console.warn('⚠️ Analytics error logging timeout');
    }, 5000);

    fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(errorData),
    })
      .then(response => {
        clearTimeout(timeoutId);
        if (!response.ok) {
          console.warn(`⚠️ Analytics error logging failed: ${response.status}`);
        } else {
          debugConsole.log('✅ Critical error logged remotely');
        }
      })
      .catch(error => {
        clearTimeout(timeoutId);
        console.warn(
          '⚠️ Failed to log analytics error remotely:',
          error.message
        );
      });
  } catch (error) {
    console.warn('⚠️ Critical error logging setup failed:', error);
  }
};

/**
 * Track analytics error patterns and log when thresholds are exceeded
 * @param {string} errorType - Type of error being tracked
 * @param {boolean} isSuccess - Whether the operation was successful
 */
export const trackAnalyticsErrorPattern = (() => {
  const errorPatterns = new Map();
  const PATTERN_WINDOW = 10 * 60 * 1000; // 10 minutes
  const FAILURE_THRESHOLD = 0.5; // 50% failure rate
  const MIN_EVENTS = 10; // Minimum events to consider pattern

  return (errorType, isSuccess) => {
    const now = Date.now();

    if (!errorPatterns.has(errorType)) {
      errorPatterns.set(errorType, {
        successes: 0,
        failures: 0,
        windowStart: now,
        lastReported: 0,
      });
    }

    const pattern = errorPatterns.get(errorType);

    // Reset window if expired
    if (now - pattern.windowStart > PATTERN_WINDOW) {
      pattern.successes = 0;
      pattern.failures = 0;
      pattern.windowStart = now;
    }

    // Track event
    if (isSuccess) {
      pattern.successes++;
    } else {
      pattern.failures++;
    }

    const totalEvents = pattern.successes + pattern.failures;
    const failureRate = totalEvents > 0 ? pattern.failures / totalEvents : 0;

    // Check if we should report a pattern
    if (
      totalEvents >= MIN_EVENTS &&
      failureRate >= FAILURE_THRESHOLD &&
      now - pattern.lastReported > PATTERN_WINDOW
    ) {
      pattern.lastReported = now;

      logCriticalAnalyticsError(
        'tracking_failure_pattern',
        `High failure rate detected for ${errorType}`,
        {
          errorType,
          failureRate: Math.round(failureRate * 100) + '%',
          totalEvents,
          successes: pattern.successes,
          failures: pattern.failures,
          windowMinutes: PATTERN_WINDOW / 60000,
        },
        'warning'
      );
    }
  };
})();

// Simple debug utilities that mirror debugConsole
export const debugLog = (...args) => debugConsole.log(...args);
export const debugInfo = (...args) => debugConsole.info(...args);
export const debugWarn = (...args) => debugConsole.warn(...args);
export const debugError = (...args) => debugConsole.error(...args);
export const debugSuccess = (...args) => debugConsole.success(...args);
export const debugFailure = (...args) => debugConsole.failure(...args);

// Re-export for convenience
export { isDebugMode };
